/**
 * Centralized Menu Configuration System
 *
 * This file contains all menu configuration for the application's settings menus.
 * It provides a unified system for determining which menu items should be visible
 * based on user roles, permissions, and company settings.
 *
 * Usage:
 * - Import `getVisibleMenuItems` to get all menu items a user can see
 * - Import `getVisibleMenuItemsWithSubMenus` to get menu items with filtered submenus
 * - Import `MENU_ITEMS` to access the raw menu configuration
 *
 * Adding new menu items:
 * 1. Add the route constant to constants/routes.ts
 * 2. Add the menu item to the MENU_ITEMS array below
 * 3. Set appropriate visibility (roles) and conditions
 * 4. Add icon in `features/profile/profileMenuItems.tsx` if the profile popover needs one
 *
 * The system automatically handles:
 * - Role-based visibility (CLIENT_ROLE, RECRUITER_ROLE)
 * - Custom conditions (e.g., !user?.is_sourcer)
 * - Permission-based submenu filtering
 * - Company feature flags
 */

import { ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE } from '@/constants/roles';
import {
  PROFILE_SETTINGS,
  NOTIFICATIONS_SETTINGS,
  ATS_SETTINGS,
  ATS_DEFAULT_MAPPINGS,
  RECRUITER_COMPANY_SETTINGS,
  RECRUITER_COMPANY_TYPE_SETTINGS,
  RECRUITER_PROJECTS,
  PERMISSIONS_SETTINGS,
} from '@/constants/routes';

import type { Company } from '@/types/company';
import type { User } from '@/types/user';
import type { StepConfig } from '@/UI/StepsForm/types';

// Type definitions for menu items and submenus
interface SubMenuItem {
  title: string;
  value: string;
  route: string;
  requiresPermission?: string;
  condition?: (user: User | null) => boolean;
}

interface MenuItem {
  title: string;
  value: string;
  visibility: string[];
  route: string;
  condition?: (user: User | null) => boolean;
  subMenus?: SubMenuItem[];
}

// Centralized menu configuration
const MENU_ITEMS: MenuItem[] = [
  {
    title: 'PROFILE_SETTINGS',
    value: 'profile',
    visibility: [ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE],
    route: PROFILE_SETTINGS,
  },
  {
    title: 'RECRUITER_COMPANY_SETTINGS',
    value: 'recruiter-company',
    visibility: [RECRUITER_ROLE],
    route: RECRUITER_COMPANY_SETTINGS,
    condition: (user: User | null) => !user?.is_sourcer,
    subMenus: [
      {
        title: 'TEAM_MEMBERS',
        value: 'recruiter-company-team',
        route: RECRUITER_COMPANY_SETTINGS,
        condition: (user: User | null) => !user?.is_sourcer,
      },
      {
        title: 'PROJECTS',
        value: 'recruiter-projects',
        route: RECRUITER_PROJECTS,
        condition: (user: User | null) => !user?.is_sourcer,
      },
      {
        title: 'COMPANY_TYPE',
        value: 'recruiter-company-type',
        route: RECRUITER_COMPANY_TYPE_SETTINGS,
        condition: (user: User | null) => !user?.is_sourcer,
      },
    ],
  },
  {
    title: 'PORTAL_SETTINGS',
    value: 'permissions',
    visibility: [CLIENT_ROLE],
    route: PERMISSIONS_SETTINGS,
    condition: (user: User | null) => {
      return user?.permissions?.view_settings === true;
    },
  },
  {
    title: 'ats.ATS_SETTINGS_TITLE',
    value: 'ats',
    visibility: [CLIENT_ROLE],
    route: ATS_SETTINGS,
    subMenus: [
      {
        title: 'ats.DEFAULT_MAPPINGS_TITLE',
        value: 'default-mappings',
        route: ATS_DEFAULT_MAPPINGS,
        requiresPermission: 'can_set_default_stage_mappings',
      },
    ],
  },
  {
    title: 'NOTIFICATION_TITLE',
    value: 'notification',
    visibility: [RECRUITER_ROLE],
    route: NOTIFICATIONS_SETTINGS,
  },
];

/**
 * Determines if a menu item should be visible based on user role and conditions
 */
export const isMenuItemVisible = (
  menuItem: MenuItem,
  user: User | null,
): boolean => {
  // Check role visibility
  if (!user?.role || menuItem.visibility.indexOf(user.role) === -1) {
    return false;
  }

  // Check custom condition
  if (typeof menuItem.condition === 'function' && !menuItem.condition(user)) {
    return false;
  }

  return true;
};

/**
 * Filters submenu items based on permissions and conditions
 */
export const getFilteredSubMenus = (
  subMenus: SubMenuItem[] | undefined,
  user: User | null,
  company: Company | null,
): SubMenuItem[] => {
  if (!subMenus) return [];

  return subMenus.filter((subMenu) => {
    // Check if permission is required and if the company has it
    const permissionCheck =
      !('requiresPermission' in subMenu) ||
      (subMenu.requiresPermission &&
        company?.[subMenu.requiresPermission as keyof typeof company]);
    // Check if a condition exists and if it's met by the user
    const conditionCheck =
      !('condition' in subMenu) ||
      (subMenu.condition && subMenu.condition(user));
    // Include the submenu if both checks pass
    return permissionCheck && conditionCheck;
  });
};

/**
 * Gets all visible menu items for a user based on their role, permissions, and company settings
 */
export const getVisibleMenuItems = (user: User | null): MenuItem[] => {
  return MENU_ITEMS.filter((menuItem) => isMenuItemVisible(menuItem, user));
};

/**
 * Gets all visible menu items with their filtered submenus
 */
export const getVisibleMenuItemsWithSubMenus = (
  user: User | null,
  company: Company | null,
): MenuItem[] => {
  return getVisibleMenuItems(user).map((menuItem) => ({
    ...menuItem,
    subMenus: getFilteredSubMenus(menuItem.subMenus, user, company),
  }));
};

interface BuildSettingsStepsParams {
  user: User | null;
  company: Company | null;
  pathname: string;
  t: (key: string) => string;
  navigate: (route: string) => void;
}

function isPathMatchingRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function longestMatchingRoute(
  pathname: string,
  routes: string[],
): string | undefined {
  return routes
    .filter((route) => isPathMatchingRoute(pathname, route))
    .sort((a, b) => b.length - a.length)[0];
}

export function buildSettingsSteps({
  user,
  company,
  pathname,
  t,
  navigate,
}: BuildSettingsStepsParams): StepConfig[] {
  return getVisibleMenuItemsWithSubMenus(user, company).map((menuItem) => {
    const filteredSubMenus = menuItem.subMenus ?? [];
    const hasSubMenus = filteredSubMenus.length > 0;
    const matchingSubRoute = longestMatchingRoute(
      pathname,
      filteredSubMenus.map((sub) => sub.route),
    );
    const isAnySubMenuActive = Boolean(matchingSubRoute);
    const isThisMainMenuRouteActive = isPathMatchingRoute(
      pathname,
      menuItem.route,
    );
    const isMainMenuHighlighted =
      isThisMainMenuRouteActive && !isAnySubMenuActive;

    const children: StepConfig[] | undefined = hasSubMenus
      ? filteredSubMenus.map((subMenu) => ({
          key: subMenu.value,
          label: t(subMenu.title),
          active: matchingSubRoute === subMenu.route,
          onClick: () => navigate(subMenu.route),
        }))
      : undefined;

    return {
      key: menuItem.value,
      label: t(menuItem.title),
      active: isMainMenuHighlighted,
      onClick: () => navigate(menuItem.route),
      children,
    };
  });
}
